import React from 'react'
import { Redirect, useParams } from 'react-router-dom'
import { useFlagsStatus } from '@unleash/proxy-client-react'
import Loader from 'src/common/Loader'
import { useResurrectionFlags } from './hooks/useResurrectionFlags'
import { useResurrectionController } from './hooks/useResurrectionController'
import { useResurrectionData } from './hooks/useResurrectionData'
import OldResurrectionPage from 'src/screens/resurrection/OldResurrectionPage'
import ResurrectionPage from 'src/screens/resurrection/ResurrectionPage'

interface ResurrectionLandingRouterProps {
  actions: any
  subscriptionInfo?: {
    status: string
    currentCredit?: number
  } | null
  isReactivating: boolean
  authed: boolean
  location: {
    pathname: string
    search: string
  }
  text: string
  conditionsText?: string
  browserTitle?: string
  coupon?: string
  prepaidOfferId?: string
  heroTitle?: string
  heroSubtitle?: string
  resurrectionCampaign?: any
  isResurrectionCampaignLoading: boolean
  featuredMeals?: any
}

interface RouteParams {
  campaignSlug?: string
}

const ResurrectionLandingRouter: React.FC<ResurrectionLandingRouterProps> = props => {
  const { flagsReady } = useFlagsStatus()
  const { campaignSlug } = useParams<RouteParams>()
  const { contentfulOn, redesignOn } = useResurrectionFlags()

  // Get unified data from both Contentful and legacy sources
  const resurrectionData = useResurrectionData({
    campaignSlug,
    actions: props.actions,
    isResurrectionCampaignLoading: props.isResurrectionCampaignLoading,
    resurrectionCampaign: props.resurrectionCampaign,
    featuredMeals: props.featuredMeals,
    heroTitle: props.heroTitle,
    heroSubtitle: props.heroSubtitle,
    text: props.text,
    conditionsText: props.conditionsText,
    browserTitle: props.browserTitle,
    coupon: props.coupon,
    prepaidOfferId: props.prepaidOfferId
  })

  // Get controller logic
  const {
    shouldShowLoader,
    shouldRedirectToValidateCC
  } = useResurrectionController({
    location: props.location,
    subscriptionInfo: props.subscriptionInfo,
    isReactivating: props.isReactivating,
    authed: props.authed,
    actions: props.actions,
    resurrectionCampaign: props.resurrectionCampaign,
    isResurrectionCampaignLoading: resurrectionData.isLoading, // Use unified loading state
    flagsReady,
    coupon: props.coupon,
    prepaidOfferId: props.prepaidOfferId
  })

  // Handle redirects
  if (shouldRedirectToValidateCC) {
    return <Redirect to="/validate-cc" />
  }

  // Handle loading states
  if (shouldShowLoader || !flagsReady || resurrectionData.isLoading) {
    return <Loader />
  }

  // Route to legacy page if Contentful is disabled
  if (!contentfulOn) {
    return (
      <OldResurrectionPage
        {...props}
        subscriptionInfo={props.subscriptionInfo ?? null}
        resurrectionCampaign={props.resurrectionCampaign ?? null}
        featuredMeals={props.featuredMeals ?? null}
        conditionsText={props.conditionsText ?? ''}
      />
    )
  }

  // Route to redesign if enabled, otherwise use current Contentful page
  if (!redesignOn) {
    return (
      <ResurrectionPage
        {...props}
        subscriptionInfo={props.subscriptionInfo ?? undefined}
        conditionsText={props.conditionsText ?? ''}
      />
    )
  }

  // For now, when redesign is enabled, we'll still use the current ResurrectionPage
  // In the next steps, we'll create the new modular sections
  return (
    <ResurrectionPage
      {...props}
      subscriptionInfo={props.subscriptionInfo ?? undefined}
      conditionsText={props.conditionsText ?? ''}
    />
  )
}

export default ResurrectionLandingRouter
